/**
 * This device's Phase 7 pairing credential (docs/android/PHASE-7-
 * ARCHITECTURE.md SS3), issued once by `redeemDevicePairingCode()`
 * (lib/deviceApi.ts, called from DevicePairingModal.tsx) and sent back as
 * `X-Device-Secret` on every device-authenticated call thereafter
 * (apps/api/src/http/middleware/deviceAuth.ts).
 *
 * Persisted the same way `persistedDeviceId()` is (lib/deviceId.ts): plain
 * `localStorage`, best-effort, with the same accepted consequence - a wiped
 * WebView storage just means this device pairs again (a fresh admin-
 * generated code), exactly like a wiped storage today means DeviceSetup.tsx
 * asks to be registered again.
 *
 * Deliberately a SEPARATE identity from Phase 6's persistedDeviceId()/
 * deviceIdForAuth(): this credential is for the newer Phase 7 fleet-
 * monitoring surface (status/health/heartbeat, DeviceManagement.tsx) only,
 * never sent with an ordinary user sign-in/refresh call, and a device can
 * run POS Billing for months on Phase 6 registration alone without ever
 * holding one of these - see DevicePairingModal.tsx's own file doc.
 */
import { nativeSecureStore } from './androidBridge';

const DEVICE_SECRET_KEY = 'kortex.deviceSecret';

/*
 * On an Android shell with a secret store (bridge ≥ 1.3) the secret lives
 * there, encrypted under an Android Keystore key, and never in WebView
 * localStorage; one left in localStorage by an older build moves across the
 * first time it is read. Elsewhere (a browser, an older APK) localStorage is
 * all there is.
 */
export function getDeviceSecret(): string | null {
  const secure = nativeSecureStore();
  if (secure) {
    const stored = secure.get(DEVICE_SECRET_KEY);
    if (stored) return stored;
    const legacy = readLocal();
    if (legacy && secure.set(DEVICE_SECRET_KEY, legacy)) {
      removeLocal();
    }
    return legacy;
  }
  return readLocal();
}

function readLocal(): string | null {
  try {
    return window.localStorage.getItem(DEVICE_SECRET_KEY);
  } catch {
    return null;
  }
}

function removeLocal(): void {
  try {
    window.localStorage.removeItem(DEVICE_SECRET_KEY);
  } catch {
    // best-effort
  }
}

export function setDeviceSecret(secret: string): void {
  const secure = nativeSecureStore();
  if (secure?.set(DEVICE_SECRET_KEY, secret)) {
    removeLocal();
    return;
  }
  try {
    window.localStorage.setItem(DEVICE_SECRET_KEY, secret);
  } catch {
    // Storage unavailable: pairing "succeeded" server-side but this device
    // will not remember it, and every heartbeat will keep failing NOT_PAIRED
    // until it is re-paired - the same accepted failure mode as every other
    // localStorage write in this app (deviceId.ts, posData/index.ts's mode
    // flags).
  }
}

export function hasDeviceCredential(): boolean {
  return getDeviceSecret() !== null;
}

/** Used when the server stops recognising this device's secret (re-paired
 *  elsewhere, or decommissioned then reissued) - see lib/deviceHeartbeat.ts's
 *  401 handling. Never called just to "log out" a device; there is no such
 *  concept here, only paired/not-paired. */
export function clearDeviceCredential(): void {
  nativeSecureStore()?.remove(DEVICE_SECRET_KEY);
  removeLocal();
}
